import { useState, useEffect, useCallback, useMemo } from 'react';
import apiService from '../services/api';

// Generic hook for API calls
export const useApi = (apiCall, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiCall();
      setData(result);
    } catch (err) {
      setError(err.message);
      console.error('API call failed:', err);
    } finally {
      setLoading(false);
    }
  }, [apiCall, ...dependencies]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetch = () => {
    fetchData();
  };

  return { data, loading, error, refetch };
};

// Hook for brands  
export const useBrands = () => {
  const getBrands = useCallback(() => apiService.getBrands(), []);
  return useApi(getBrands);
};

// Hook for persisted brand selection across all pages
export const useSelectedBrand = () => {
  const [selectedBrandId, setSelectedBrandIdState] = useState(() => {
    const stored = localStorage.getItem('selectedBrandId');
    return stored ? Number(stored) : null;
  });
  const { data: brands, loading } = useBrands();

  const setSelectedBrandId = useCallback((id) => {
    const numId = Number(id);
    setSelectedBrandIdState(numId);
    localStorage.setItem('selectedBrandId', String(numId));
  }, []);

  // Auto-select: use stored value if valid, otherwise first brand
  useEffect(() => {
    if (!brands?.results?.length) return;
    const ids = brands.results.map(b => b.id);
    if (selectedBrandId && ids.includes(selectedBrandId)) return;
    setSelectedBrandId(ids[0]);
  }, [brands, selectedBrandId, setSelectedBrandId]);

  return {
    selectedBrandId,
    setSelectedBrandId,
    brands: brands?.results || [],
    brandsLoading: loading,
  };
};

// Hook for a specific brand
export const useBrand = (brandId) => {
  const getBrand = useCallback(() => apiService.getBrand(brandId), [brandId]);
  return useApi(getBrand, [brandId]);
};

// Hook for posts
export const usePosts = (filters = {}) => {
  // Memoize the serialized filters to prevent unnecessary re-renders
  const serializedFilters = useMemo(() => JSON.stringify(filters), [JSON.stringify(filters)]);
  const getPosts = useCallback(() => apiService.getPosts(filters), [filters]);
  return useApi(getPosts, [serializedFilters]);
};

// Hook for a specific post
export const usePost = (postId) => {
  return useApi(() => apiService.getPost(postId), [postId]);
};

// Hook for social accounts
export const useSocialAccounts = (brandId = null) => {
  const getSocialAccounts = useCallback(() => apiService.getSocialAccounts(brandId), [brandId]);
  return useApi(getSocialAccounts, [brandId]);
};

// Hook for analytics
export const useAnalytics = (brandId = null) => {
  return useApi(() => apiService.getAnalytics(brandId), [brandId]);
};

// Hook for brand analytics summary
export const useBrandAnalytics = (brandId) => {
  return useApi(() => apiService.getBrandAnalyticsSummary(brandId), [brandId]);
};

// Hook for ideas
export const useIdeas = (filters = {}) => {
  const serializedFilters = useMemo(() => JSON.stringify(filters), [JSON.stringify(filters)]);
  const getIdeas = useCallback(() => apiService.getIdeas(filters), [filters]);
  return useApi(getIdeas, [serializedFilters]);
};

// Hook for influence items
export const useInfluenceItems = (filters = {}) => {
  const serializedFilters = useMemo(() => JSON.stringify(filters), [JSON.stringify(filters)]);
  const getItems = useCallback(() => apiService.getInfluenceItems(filters), [filters]);
  return useApi(getItems, [serializedFilters]);
};

// Hook for managing async operations
export const useAsyncOperation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (operation) => {
    try {
      setLoading(true);
      setError(null);
      const result = await operation();
      return { success: true, data: result };
    } catch (err) {
      setError(err.message);
      console.error('Async operation failed:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = () => {
    setLoading(false);
    setError(null);
  };

  return { loading, error, execute, reset };
};

// Hook for form submissions
export const useFormSubmit = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const submit = useCallback(async (submitFunction) => {
    try {
      setSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(false);
      
      const result = await submitFunction();
      setSubmitSuccess(true);
      return { success: true, data: result };
    } catch (error) {
      setSubmitError(error.message);
      return { success: false, error: error.message };
    } finally {
      setSubmitting(false);
    }
  }, []);

  const reset = () => {
    setSubmitting(false);
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  return {
    submitting,
    submitError,
    submitSuccess,
    submit,
    reset,
  };
};
